import { useState, type CSSProperties, type ReactNode, type ChangeEvent, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { useToasts, useAuth } from './state';
import { buildReviewUrl, markReviewCompleted } from './components/ReviewBanner';
import { AccountAccessPill } from './nav/AccountAccess';

// ---------- Button ----------
export interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'brass' | 'spark' | 'danger';
  type?: 'button' | 'submit';
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  block?: boolean;
  compact?: boolean;
  href?: string;
  external?: boolean;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

export function Button(props: ButtonProps) {
  const {
    children,
    variant = 'primary',
    type = 'button',
    onClick,
    disabled,
    loading,
    block,
    compact,
    href,
    external,
    className,
    style,
    ariaLabel,
  } = props;
  const cls = [
    'btn',
    `btn--${variant}`,
    block && 'btn--block',
    compact && 'btn--compact',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const inner = loading ? <span className="btn__spinner" /> : <>{children}</>;

  if (href) {
    if (external) {
      return (
        <a
          className={cls}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={style}
          aria-label={ariaLabel}
        >
          {inner}
        </a>
      );
    }
    return (
      <Link href={href} className={cls} style={style} aria-label={ariaLabel}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={cls}
      onClick={onClick}
      disabled={disabled || loading}
      style={style}
      aria-label={ariaLabel}
    >
      {inner}
    </button>
  );
}

// ---------- Verdict badge ----------
/**
 * Focus star — the inventor's "work on this next" marker. Focused ideas
 * show a filled brass star; unfocused show a faint outline star that
 * brightens on hover (and stays tappable on touch). Used in every idea
 * list (Ideas page, dashboard) and the idea detail page. Pure UI: the
 * caller owns the optimistic toggle.
 *
 * Renders as a real button. Callers inside a clickable row must stop
 * propagation so toggling never navigates into the idea.
 */
export function FocusStar({
  focused,
  onToggle,
  size = 18,
}: {
  focused: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      className={`focus-star${focused ? ' is-focused' : ''}`}
      aria-pressed={focused}
      aria-label={focused ? 'Unfocus this idea' : 'Focus this idea'}
      title={focused ? 'Focused — tap to unfocus' : 'Focus this idea to work on it next'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 3.2l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.9l-5.2 2.74.99-5.79-4.21-4.1 5.82-.85L12 3.2z"
          fill={focused ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export function VerdictBadge({ verdict }: { verdict: string | null | undefined }) {
  // The Capture page passes `idea.status` as the verdict prop, which
  // can be one of: drafting | in_session | memo_ready | archived. The
  // Strategy page passes the actual memo verdict: go | pivot | stop.
  // We accept both vocabularies and translate to a single label set so
  // the same badge component works on both surfaces.
  const v = verdict ?? 'draft';
  const label =
    v === 'go'
      ? 'Go'
      : v === 'pivot'
        ? 'Pivot'
        : v === 'stop'
          ? 'Stop'
          : v === 'compiling'
            ? 'Compiling'
            : v === 'in_session'
              ? 'Session'
              : v === 'memo_ready'
                // memo_ready as the idea status means the strategy session
                // wrapped and a memo was generated. Without the verdict
                // here, "Memo ready" reads correctly and points the user
                // toward Strategy to read it.
                ? 'Memo ready'
                : v === 'archived'
                  ? 'Archived'
                  : v === 'drafting'
                    ? 'Draft'
                    : 'Draft';
  const cls = `verdict verdict--${
    v === 'in_session'
      ? 'in-session'
      : v === 'memo_ready'
        ? 'go'
        : v === 'drafting' || v === 'archived'
          ? 'draft'
          : v
  }`;
  return <span className={cls}>{label}</span>;
}

// ---------- Caption ----------
export function Caption({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['section-label', className].filter(Boolean).join(' ')}>{children}</div>;
}

// ---------- Streak indicator ----------
export function StreakIndicator({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div className="streak-pill">
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M5.5 0.5 L11 10.5 L0 10.5 Z" fill="var(--brass)" />
      </svg>
      <span>
        {count}-day streak
      </span>
    </div>
  );
}

// ---------- Brand seal: brass-embossed serif M on a heartwood field ----------
//
// The mark reads as a personal wax seal / IP signet. The "M" speaks directly
// to "Me" — the user's idea, owned and stamped. Sized to scale across the
// whole product (16px favicon to 96px memo cover).
//
// Component name kept as `CompassSeal` to avoid churn across imports; the
// visual is now the M-seal mark.
export function CompassSeal({ size = 32, className, animate }: { size?: number; className?: string; animate?: boolean }) {
  const style: CSSProperties = animate
    ? { animation: 'sealIn 400ms cubic-bezier(.2,.7,.3,1.2) both' }
    : {};
  // Unique IDs so multiple seals on the same page don't share gradient defs
  const idBase = `ms-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <defs>
        {/* Subtle radial highlight on the brass to fake the embossed feel */}
        <radialGradient id={`${idBase}-brass`} cx="0.4" cy="0.35" r="0.85">
          <stop offset="0%" stopColor="#D4A663" />
          <stop offset="55%" stopColor="#B5853C" />
          <stop offset="100%" stopColor="#8C6322" />
        </radialGradient>
        <radialGradient id={`${idBase}-bg`} cx="0.5" cy="0.45" r="0.7">
          <stop offset="0%" stopColor="#1F4233" />
          <stop offset="100%" stopColor="#102219" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill={`url(#${idBase}-bg)`} />
      <circle cx="32" cy="32" r="29.4" fill="none" stroke="#B5853C" strokeWidth="0.5" opacity="0.35" />
      {/* Serif M — drawn as paths so it does not depend on font availability
          for the seal to render correctly even before fonts have loaded. */}
      <path
        d="M14 47 L14 17.5 L20 17.5 L32 38 L44 17.5 L50 17.5 L50 47 L45.5 47 L45.5 25.4 L33.6 47 L30.4 47 L18.5 25.4 L18.5 47 Z"
        fill={`url(#${idBase}-brass)`}
      />
      {/* Brass highlight on the top edge of the M strokes */}
      <path
        d="M14 17.5 L20 17.5 L32 38 L44 17.5 L50 17.5"
        stroke="#E0BC7C"
        strokeWidth="0.4"
        fill="none"
        opacity="0.6"
      />
    </svg>
  );
}

// ---------- Advisor avatar (chat bubble persona) ----------
//
// The AI advisor's avatar in the strategy session chat. Uses the SAME
// walnut/brain glyph that lives in the wordmark and the favicon —
// byte-identical path data — so every brand surface (browser tab, OG
// card, wordmark, advisor avatar, PDF seal) speaks one visual language.
// Brass walnut on a heartwood disc.
export function AdvisorAvatar({ size = 28, className }: { size?: number; className?: string }) {
  const idBase = `aa-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`${idBase}-bg`} cx="0.5" cy="0.45" r="0.7">
          <stop offset="0%" stopColor="#1F4233" />
          <stop offset="100%" stopColor="#102219" />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="15" fill={`url(#${idBase}-bg)`} />
      <circle
        cx="16"
        cy="16"
        r="14.6"
        fill="none"
        stroke="#B5853C"
        strokeWidth="0.4"
        opacity="0.3"
      />
      {/* The EntreDash ascending-spark mark (on-dark colors: brass ember +
          spark-light staircase/arrowhead), centered in the dark disc — replaces
          the retired Me'Invent two-lobe brain glyph. Nested svg with the mark's
          content-bounds viewBox (5 30 86 62) scales + centers it cleanly. */}
      <svg x="6" y="8.4" width="20" height="15.2" viewBox="2 26 92 70">
        {/* Ribbon drawn first and started inside the node (M12) so the node
            paints on top as a full round dot with the stepper emerging
            seamlessly from its right edge — no background sliver at the join. */}
        <path d="M12 80 H44 V64 H62 V48 H76" fill="none" stroke="#4FD992" strokeWidth="12" strokeLinejoin="miter" />
        <polygon points="91,30 73,37 83,55" fill="#4FD992" />
        <circle cx="16" cy="80" r="10" fill="#C79A4E" />
        <circle cx="11" cy="67" r="2.6" fill="#C79A4E" />
        <circle cx="7" cy="58" r="1.7" fill="#C79A4E" />
        <circle cx="13" cy="51" r="1.1" fill="#C79A4E" />
      </svg>
    </svg>
  );
}

// ---------- Brand: EntreDash ----------
//
// The identity is the ascending-spark mark + the "EntreDash" wordmark.
// See src/interfaces/@brand/entredash-identity.md for the full system.
// "Entre" is grounded (inherits ink/parchment), "Dash" is Spark Green and
// leans forward for momentum. The retired MeInvent brain-glyph mark is gone.

/**
 * The ascending-spark mark — a brass ignition node, a diminishing ember
 * trail, a spark-green staircase, and a NE arrowhead. Flat vector, exact
 * geometry from the identity spec (viewBox 0 0 96 96). `onDark` switches to
 * the lighter spark-green + lighter brass for heartwood grounds.
 */
export function AscendingSparkMark({
  size = 28,
  onDark = false,
  className,
}: {
  /** px number, or any CSS length string (e.g. "1.32em") so the mark can scale
      with an inherited font-size in BrandLogo's size="inherit" mode. */
  size?: number | string;
  onDark?: boolean;
  className?: string;
}) {
  // The onDark prop sets the default green/brass. Both are exposed as CSS
  // custom-property fallbacks (--ed-mark-green / --ed-mark-brass) so an
  // ancestor can override them per-context WITHOUT changing default behavior —
  // e.g. the nav brightens them only in its transparent over-heartwood state.
  // Because the literals live in the var() fallback (not set on the element),
  // an inherited ancestor value wins; unset = the onDark-derived literal.
  const green = onDark ? '#4FD992' : '#1F9D63';
  const brass = onDark ? '#C79A4E' : '#B5853C';
  const greenFill = `var(--ed-mark-green, ${green})`;
  const brassFill = `var(--ed-mark-brass, ${brass})`;
  // The drawn content spans y≈30 (arrow tip) to y≈92 (node bottom), so its
  // visual center is ~61 — well below the 0–96 box center of 48. Left as-is,
  // the mark reads bottom-heavy and sits low next to the wordmark. Offsetting
  // the viewBox up by 13 (window y 13→109) gives the content equal ~17u
  // margins top and bottom, so it optically centers against the wordmark.
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 13 96 96"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* ascending staircase ribbon — drawn first and started inside the node
          (M12) so the node paints on top as a full round dot with the stepper
          emerging seamlessly from its right edge, no sliver at the join. */}
      <path
        d="M12 80 H44 V64 H62 V48 H76"
        fill="none"
        stroke={greenFill}
        strokeWidth="12"
        strokeLinejoin="miter"
        strokeLinecap="butt"
      />
      {/* NE arrowhead */}
      <polygon points="91,30 73,37 83,55" fill={greenFill} />
      {/* brass ignition node + diminishing ember trail, painted on top */}
      <circle cx="16" cy="80" r="10" fill={brassFill} />
      <circle cx="11" cy="67" r="2.6" fill={brassFill} />
      <circle cx="7" cy="58" r="1.7" fill={brassFill} />
      <circle cx="13" cy="51" r="1.1" fill={brassFill} />
    </svg>
  );
}

/**
 * The EntreDash wordmark — one tightly-kerned word in the display face,
 * "Dash" in Spark Green with a forward lean. Use anywhere the brand name
 * appears as text (replaces the literal string "EntreDash"/"MeInvent").
 *
 * `size`:
 *   - number (px) — fixed display size
 *   - `"inherit"` — match surrounding text (body copy, FAQ, legal, footer)
 *   - omitted — defaults to 19px
 * `onDark` — use the lighter spark-green for "Dash" on heartwood grounds.
 */
export function Wordmark({
  size = 19,
  className,
  onDark = false,
  withTM = false,
  noAccent = false,
}: {
  size?: number | 'inherit';
  className?: string;
  onDark?: boolean;
  /** Append the trademark mark (™) after "Dash". Reserved for the large
      canonical lockup (footer / share asset), not small UI instances. */
  withTM?: boolean;
  /** Suppress the brass entré accent over the final "e". Used on the more
      buttoned-up B2B surfaces (the /ve landing) where the flourish reads as
      an apostrophe and a cleaner monochrome-plus-green wordmark is wanted.
      Colors and the two-tone Entre/Dash split are unchanged — only the accent
      is dropped. */
  noAccent?: boolean;
}) {
  return (
    <span
      className={[
        'brand-mark',
        onDark ? 'brand-mark--on-dark' : '',
        noAccent ? 'brand-mark--no-accent' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={size === 'inherit' ? undefined : { fontSize: size }}
    >
      {/* The final "e" of "Entre" carries a brass acute accent (.brand-e::after
          in global.css) — the entré/entrée flourish, also a tiny dash echoing
          the Dash. The "e" keeps the base color; only the accent is brass.
          `noAccent` suppresses it via .brand-mark--no-accent. */}
      Entr<span className="brand-e">e</span>
      <span className="brand-dash">Dash</span>
      {withTM && <span className="brand-tm">&trade;</span>}
    </span>
  );
}

/**
 * The compact brand lockup — ascending-spark mark + wordmark, side by side.
 * Used in nav bars and other logo placements. The full tagline lockup
 * ("Spark to business" / "Invent, by Me") lives in the share/OG asset, not
 * inline UI. `size` sets the wordmark size; the mark scales to ~1.32× it.
 */
export function BrandLogo({
  size = 24,
  onDark = false,
  className,
  withTM = false,
  tagline = false,
}: {
  /** number (px) for a fixed lockup, or "inherit" to scale with the
      surrounding font-size (the mark follows via an em-based size). */
  size?: number | 'inherit';
  onDark?: boolean;
  className?: string;
  /** Append the ™ to the wordmark (canonical large lockup only). */
  withTM?: boolean;
  /** Render the full canonical lockup: the wordmark with the two taglines
      ("Spark to business" left / "Invent, by Me" right) justified beneath it,
      spanning the wordmark width. The mark sits to the left of that column. */
  tagline?: boolean;
}) {
  const inherit = size === 'inherit';
  // The mark is ~1.32× the wordmark: a px multiple for fixed sizes, an em
  // multiple when inheriting so it tracks the container's font-size.
  const markSize = inherit ? '1.32em' : Math.round((size as number) * 1.32);

  const word = <Wordmark size="inherit" onDark={onDark} withTM={withTM} />;

  return (
    <span
      className={['brand-logo', tagline ? 'brand-logo--lockup' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={{
        display: 'inline-flex',
        // In tagline mode the mark aligns to the top of the wordmark column;
        // otherwise it centers against the wordmark line.
        alignItems: tagline ? 'flex-start' : 'center',
        gap: '0.32em',
        ...(inherit ? {} : { fontSize: size }),
        lineHeight: 1,
        // The forward-leaning "Dash" glyph overhangs its text box by a few px;
        // reserve em-scaled room on the right so it never clips inside a
        // flex-shrink:0 nav slot or other tight container.
        paddingRight: '0.24em',
      }}
    >
      {/* The wordmark caps ("EntreDash" has no descenders) sit high in their
          line box, so flex-centering aligns the mark to the line-box center —
          slightly below the visible caps' center. Lift the mark a hair
          (em-based, so it scales with the logo size) to optically center it
          against the caps. In lockup mode the row aligns flex-start (so the
          taglines hang below the wordmark), and this larger negative top pulls
          the mark up to center on the WORDMARK — matching the nav standard —
          rather than sitting low against the full wordmark+taglines column. */}
      <span
        style={{
          position: 'relative',
          top: tagline ? '-0.22em' : '-0.07em',
          display: 'inline-flex',
          flex: 'none',
        }}
      >
        <AscendingSparkMark size={markSize} onDark={onDark} />
      </span>
      {tagline ? (
        <span className="brand-logo__col">
          {word}
          <span className="brand-logo__taglines" aria-hidden="true">
            <span className="brand-logo__tag-spark">Spark to business</span>
            <span className="brand-logo__tag-own">Invent, by Me</span>
          </span>
        </span>
      ) : (
        word
      )}
    </span>
  );
}

// ---------- Toasts ----------
// A "registry slip": dark Heartwood bar, top-center, stamp on the left and a
// mono dateline on the right. The dateline is genuinely useful on admin screens
// where several actions fire within seconds — it tells you whether the
// confirmation you are reading belongs to the click you just made.
export function ToastDeck() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);
  const pause = useToasts((s) => s.pause);
  const resume = useToasts((s) => s.resume);

  // Screen readers get the two live regions below, not the visual deck, so the
  // mono timestamp is not read aloud after every message. The regions are
  // always mounted and only their text changes: mounting a live region at the
  // same moment as its content is unreliable across screen reader and browser
  // combinations, and the announcement gets dropped.
  const latest = [...toasts].reverse();
  const latestPolite = latest.find((t) => t.variant !== 'error')?.message ?? '';
  const latestAlert = latest.find((t) => t.variant === 'error')?.message ?? '';

  return (
    <>
      <div className="toast-container" aria-hidden="true">
        {toasts.map((t) => {
          const variant = t.variant ?? 'brass';
          return (
            <div
              key={t.id}
              className={`toast toast--${variant}${t.leaving ? ' toast--leaving' : ''}`}
              // Hold the auto-dismiss while the user is reading or reaching for
              // the action.
              onMouseEnter={() => pause(t.id)}
              onMouseLeave={() => resume(t.id)}
              onFocus={() => pause(t.id)}
              onBlur={() => resume(t.id)}
            >
              <span className="toast__stamp">
                {variant === 'success' && (
                  <svg viewBox="0 0 24 24">
                    <path d="M5 12.5l4.5 4.5L19 7" />
                  </svg>
                )}
                {variant === 'error' && (
                  <svg viewBox="0 0 24 24">
                    <path d="M12 5.5v8.5" />
                    <path d="M12 18h.01" />
                  </svg>
                )}
                {/* brass: a bare wafer. The absent glyph is the signal. */}
              </span>
              <span className="toast__msg">{t.message}</span>
              <span className="toast__right">
                {t.action && (
                  <>
                    <span className="toast__rule" />
                    <button
                      type="button"
                      className="toast__action"
                      onClick={() => {
                        t.action!.onClick();
                        dismiss(t.id);
                      }}
                    >
                      {t.action.label}
                    </button>
                  </>
                )}
                <span className="toast__ts">
                  {new Date(t.at).toLocaleTimeString('en-GB', { hour12: false })}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {latestPolite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {latestAlert}
      </div>
    </>
  );
}

// ---------- Digit input (6-digit verification code) ----------
export function DigitInput({
  value,
  onChange,
  autoFocus,
  disabled,
  status = 'idle',
}: {
  value: string;
  onChange: (next: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Colors the boxes for feedback without changing their size. */
  status?: 'idle' | 'error' | 'success';
}) {
  const digits = (value + '      ').slice(0, 6).split('');

  const focusBox = (idx: number) => {
    const el = document.getElementById(`digit-${idx}`) as HTMLInputElement | null;
    el?.focus();
    el?.select?.();
  };

  const handleInput = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');

    // OS autofill (iOS QuickType "From Messages", Android SMS suggestion) drops
    // the ENTIRE code into whichever box is focused. Detect the multi-digit
    // value and spread it across the boxes so one-tap autofill actually
    // completes the code instead of capturing a single digit.
    if (raw.length > 1) {
      const filled = (value.slice(0, i) + raw).replace(/[^0-9]/g, '').slice(0, 6);
      onChange(filled);
      setTimeout(() => focusBox(Math.min(filled.length, 5)), 0);
      return;
    }

    // Single digit typed, or the box cleared (raw === '').
    const arr = (value + '      ').slice(0, 6).split('');
    arr[i] = raw || ' ';
    const packed = arr.join('').replace(/\s+/g, '').slice(0, 6);
    onChange(packed);
    if (raw && i < 5) setTimeout(() => focusBox(i + 1), 0);
  };

  const handleKey = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      // If this box already has a digit, let the native change clear it first.
      if (digits[i].trim()) return;
      // Empty box: clear the previous digit and step back to it.
      if (i > 0) {
        e.preventDefault();
        const arr = (value + '      ').slice(0, 6).split('');
        arr[i - 1] = ' ';
        onChange(arr.join('').replace(/\s+/g, '').slice(0, 6));
        focusBox(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < 5) {
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    setTimeout(() => focusBox(Math.min(text.length, 5)), 0);
  };

  const borderColor =
    status === 'error'
      ? 'var(--oxblood)'
      : status === 'success'
        ? 'rgba(31,157,99,0.85)'
        : 'var(--border-stronger)';
  const background =
    status === 'success' ? 'rgba(31,157,99,0.07)' : 'var(--surface-elevated)';

  return (
    <div
      className="digit-input-row"
      style={{
        display: 'flex',
        gap: 'clamp(6px, 2vw, 10px)',
        // The row can never exceed its container, and each box flexes to fill
        // it — so six boxes always fit any width from a 320px phone up, with a
        // sensible cap so they don't balloon on desktop.
        width: '100%',
        maxWidth: 336,
        boxSizing: 'border-box',
      }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          id={`digit-${i}`}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          // Not capped at 1: OS autofill needs to deliver the full code to the
          // focused box so handleInput can spread it. The controlled value keeps
          // each box visually showing a single digit.
          maxLength={6}
          value={digits[i].trim()}
          onChange={handleInput(i)}
          onKeyDown={handleKey(i)}
          onPaste={handlePaste}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`Digit ${i + 1}`}
          className="digit-input-box"
          style={{
            // Fluid: grow to share the row equally, shrink below the 54px cap
            // on narrow screens. flex-basis 0 + min-width 0 is what lets them
            // shrink past their intrinsic size so the row never overflows.
            // A fixed height (not aspect-ratio) avoids any intrinsic min width
            // that would keep the row from collapsing.
            flex: '1 1 0',
            minWidth: 0,
            maxWidth: 54,
            height: 60,
            textAlign: 'center',
            fontFamily: 'var(--f-serif)',
            fontSize: 'clamp(22px, 6vw, 28px)',
            border: `1px solid ${borderColor}`,
            borderRadius: 'var(--r-md)',
            background,
            color: 'var(--ink)',
            padding: 0,
            transition:
              'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
          }}
        />
      ))}
    </div>
  );
}

// ---------- Auth-only redirect helper ----------
export function useAuthRedirect(to: string) {
  const [location, navigate] = useLocation();
  const { user, ready } = useAuth();
  if (ready && !user && location !== to) {
    setTimeout(() => navigate(to), 0);
  }
  return user;
}

// ---------- Avatar with menu ----------
export function AvatarMenu() {
  const [open, setOpen] = useState(false);
  const profile = useAuth((s) => s.profile);
  const logout = useAuth((s) => s.logout);
  const [, navigate] = useLocation();

  const initials = (profile?.name ?? 'You')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="avatar"
        onClick={() => setOpen((s) => !s)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        aria-label="Account menu"
      >
        {initials || 'Y'}
      </button>
      {open && (
        <div className="avatar__menu" role="menu">
          <div style={{ padding: '6px 12px 10px', borderBottom: '1px solid var(--border-hairline)', marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{profile?.name || 'Inventor'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-decorative)' }}>{profile?.email ?? ''}</div>
            <div style={{ marginTop: 8 }}>
              <AccountAccessPill />
            </div>
          </div>
          <button
            className="avatar__menu-item"
            onMouseDown={(e) => {
              e.preventDefault();
              setOpen(false);
              void import('./guide/store').then((m) => m.replayGuide());
            }}
          >
            How to start
          </button>
          <button className="avatar__menu-item" onMouseDown={(e) => { e.preventDefault(); navigate('/settings'); }}>
            Settings
          </button>
          <button
            className="avatar__menu-item"
            onMouseDown={(e) => { e.preventDefault(); navigate('/settings/subscription'); }}
          >
            Subscription
          </button>
          <hr className="hairline" />
          {/* Always-available path back to the review form, so a user who
              dismissed the prompt banner can still find it here to leave a
              review. Opening it also quiets the banner prompt (they're going
              to review either way); this entry itself stays regardless. */}
          <button
            className="avatar__menu-item"
            onMouseDown={(e) => {
              e.preventDefault();
              markReviewCompleted();
              window.open(
                buildReviewUrl(profile),
                '_blank',
                'noopener,noreferrer',
              );
              setOpen(false);
            }}
          >
            Share your experience
          </button>
          <hr className="hairline" />
          <button
            className="avatar__menu-item"
            onMouseDown={async (e) => {
              e.preventDefault();
              await logout();
              navigate('/');
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Loading states ----------
//
// `BrandLoader` is the canonical in-app loading visual. A thin brass ring
// rotating slowly, with the wordmark below in caption type. Replaces the
// brand seal anywhere that was being used as a "loading"/decorative spot,
// so the brand seal can stay reserved for actual document surfaces (the
// memo PDF cover and the memo viewer top-right corner).
export function BrandLoader({
  label,
  size = 'md',
}: {
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ringSize = size === 'sm' ? 28 : size === 'lg' ? 56 : 40;
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
      }}
    >
      <div
        style={{
          width: ringSize,
          height: ringSize,
          borderRadius: '50%',
          border: `${size === 'lg' ? 2 : 1.5}px solid var(--brass)`,
          borderTopColor: 'transparent',
          animation: 'spinSlow 1100ms linear infinite',
        }}
      />
      <div style={{ textAlign: 'center' }}>
        <Wordmark size={size === 'lg' ? 18 : 16} />
        {label && (
          <div
            className="f-caption"
            style={{
              marginTop: 8,
              fontSize: 10,
              color: 'var(--text-decorative)',
            }}
          >
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

export function FullPageLoader() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-page)',
      }}
    >
      <BrandLoader />
    </div>
  );
}
